import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, Brain, FileText, Users, Trophy, Settings, 
  ChevronLeft, ChevronRight, Lightbulb, FolderOpen,
  GraduationCap, BarChart3, User, LogOut, Menu, X,
  Sparkles, Target, Rocket
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';

/**
 * Unified Sidebar Navigation
 * Consolidates all workspace navigation into one clean interface
 */

const NAV_ITEMS = [
  {
    id: 'home',
    label: 'Home',
    icon: Home,
    path: '/dashboard',
    description: 'Your personal dashboard'
  },
  {
    id: 'divider-1',
    type: 'divider',
    label: 'Create'
  },
  {
    id: 'think-lab',
    label: 'Think Lab',
    icon: Brain,
    path: '/session/new',
    description: 'Start a thinking session',
    highlight: true
  },
  {
    id: 'evaluate',
    label: 'Quick Evaluate',
    icon: FileText,
    path: '/quick-evaluate',
    description: 'Evaluate a paper or idea'
  },
  {
    id: 'divider-2',
    type: 'divider',
    label: 'Community'
  },
  {
    id: 'leaderboards',
    label: 'Leaderboards',
    icon: BarChart3,
    path: '/community',
    description: 'Top innovators'
  },
  {
    id: 'challenges',
    label: 'Challenges',
    icon: Target,
    path: '/challenges',
    description: 'Innovation challenges'
  },
  {
    id: 'teams',
    label: 'Teams',
    icon: Users,
    path: '/teams/dashboard',
    description: 'Team collaboration'
  }
];

const BOTTOM_NAV_ITEMS = [
  {
    id: 'profile',
    label: 'Profile',
    icon: User,
    path: '/profile'
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    path: '/settings/security'
  }
];

const UnifiedSidebar = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Don't show sidebar on public pages
  const publicPaths = ['/', '/login', '/signup', '/auth', '/how-it-works', '/about', '/features'];
  const isPublicPage = publicPaths.some(p => location.pathname === p || location.pathname.startsWith('/auth'));
  
  if (isPublicPage || !isAuthenticated) {
    return <>{children}</>;
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={cn(
        "flex items-center gap-3 p-4 border-b",
        collapsed ? "justify-center" : ""
      )}>
        <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        {!collapsed && (
          <div>
            <h1 className="font-bold text-slate-900">Idea Fountain</h1>
            <p className="text-xs text-slate-500">Innovation Platform</p>
          </div>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {NAV_ITEMS.map((item) => {
          if (item.type === 'divider') {
            return collapsed ? (
              <div key={item.id} className="my-3 border-t border-slate-200" />
            ) : (
              <div key={item.id} className="mt-6 mb-2 px-3">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {item.label}
                </span>
              </div>
            );
          }

          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <button
              key={item.id}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all text-left",
                active
                  ? "bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 font-medium"
                  : "text-slate-600 hover:bg-slate-100",
                item.highlight && !active && "bg-gradient-to-r from-purple-50 to-pink-50",
                collapsed && "justify-center px-2"
              )}
              title={collapsed ? item.label : undefined}
            >
              <Icon className={cn(
                "w-5 h-5 flex-shrink-0",
                active ? "text-purple-600" : "text-slate-500"
              )} />
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{item.label}</div>
                  {item.description && (
                    <div className="text-xs text-slate-400 truncate">
                      {item.description}
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="border-t p-2">
        {BOTTOM_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <button
              key={item.id}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-all text-left",
                active
                  ? "bg-purple-100 text-purple-700"
                  : "text-slate-600 hover:bg-slate-100",
                collapsed && "justify-center px-2"
              )}
              title={collapsed ? item.label : undefined}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm">{item.label}</span>}
            </button>
          );
        })}

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-all text-left",
            collapsed && "justify-center px-2"
          )}
          title={collapsed ? "Logout" : undefined}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="text-sm">Logout</span>}
        </button>

        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex w-full items-center justify-center gap-2 px-3 py-2 mt-2 rounded-lg text-slate-400 hover:bg-slate-100 transition-all"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="text-xs">Collapse</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
      >
        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden lg:flex flex-col bg-white border-r shadow-sm transition-all duration-300",
          collapsed ? "w-20" : "w-64"
        )}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default UnifiedSidebar;
